import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { MagnifyingGlassIcon as MagnifyingGlass, XIcon as X, MegaphoneIcon as Megaphone, UserIcon as User } from "@phosphor-icons/react";

/**
 * Admin composer — sends an inbox message either to a hand-picked set of
 * users (individual mode) or to every non-admin (broadcast mode). Uses a
 * debounced `/admin/data/users?q=` search to resolve recipients.
 */
export default function AdminComposeDialog({ open, onClose, onSent }) {
  const [mode, setMode] = useState("individual");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]); // [{id, email, name}]

  const [broadcastConfirmOpen, setBroadcastConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMode("individual");
    setTitle("");
    setBody("");
    setLink("");
    setSearch("");
    setResults([]);
    setSelected([]);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  // Debounced user search — only active in individual mode.
  useEffect(() => {
    if (mode !== "individual" || search.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/data/users", {
          params: { q: search.trim(), limit: 8 },
        });
        if (!cancelled) setResults(data.items || []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, mode]);

  const add = (u) => {
    setSelected((prev) => (prev.find((p) => p.id === u.id) ? prev : [...prev, u]));
    setSearch("");
    setResults([]);
  };
  const remove = (id) => setSelected((prev) => prev.filter((p) => p.id !== id));

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and message are required");
      return;
    }
    if (mode === "individual" && selected.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/messages/admin-send", {
        mode,
        user_ids: mode === "individual" ? selected.map((u) => u.id) : undefined,
        title: title.trim(),
        body: body.trim(),
        link: link.trim(),
      });
      if (data.mode === "broadcast") {
        toast.success(
          `Broadcast sent to ${data.delivered} of ${data.recipients_total} users` +
          (data.opted_out ? ` (${data.opted_out} opted out)` : "")
        );
      } else {
        toast.success(
          `Delivered to ${data.delivered} user${data.delivered === 1 ? "" : "s"}` +
          (data.missing_user_ids?.length ? ` · ${data.missing_user_ids.length} not found` : "")
        );
      }
      reset();
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitOrConfirm = () => {
    if (mode === "broadcast") setBroadcastConfirmOpen(true);
    else send();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-xl" data-testid="admin-compose-dialog">
          <DialogHeader>
            <DialogTitle>Compose inbox message</DialogTitle>
            <DialogDescription>
              Deliver a message to individual users or broadcast to everyone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 block">
                Send to
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMode("individual")}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    mode === "individual"
                      ? "border-[#0E0E0E] bg-[#0E0E0E]/5 text-[#0E0E0E]"
                      : "border-[#E5E5E5] bg-white text-[#6B6B6B] hover:border-[#0E0E0E]/30"
                  }`}
                  data-testid="admin-compose-mode-individual"
                >
                  <div className="flex items-center gap-2">
                    <User size={14} weight="duotone" />
                    <span className="font-semibold text-ink">Individual users</span>
                  </div>
                  <p className="text-[11px] mt-1">Pick specific recipients. Always delivered.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("broadcast")}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    mode === "broadcast"
                      ? "border-[#C1272D] bg-[#C1272D]/5 text-[#0E0E0E]"
                      : "border-[#E5E5E5] bg-white text-[#6B6B6B] hover:border-[#C1272D]/30"
                  }`}
                  data-testid="admin-compose-mode-broadcast"
                >
                  <div className="flex items-center gap-2">
                    <Megaphone size={14} weight="duotone" />
                    <span className="font-semibold text-ink">Broadcast to all</span>
                  </div>
                  <p className="text-[11px] mt-1">Every non-admin. Respects opt-outs.</p>
                </button>
              </div>
            </div>

            {mode === "individual" && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 block">
                  Recipients * <span className="font-normal normal-case text-[#6B6B6B]">({selected.length})</span>
                </Label>
                {selected.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {selected.map((u) => (
                      <Badge
                        key={u.id}
                        variant="outline"
                        className="gap-1 pr-1 text-xs"
                        data-testid={`admin-compose-selected-${u.id}`}
                      >
                        <span className="font-mono">{u.email}</span>
                        <button
                          type="button"
                          onClick={() => remove(u.id)}
                          className="hover:bg-[#C1272D]/10 rounded-full p-0.5"
                          aria-label={`Remove ${u.email}`}
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <MagnifyingGlass
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]"
                  />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    className="pl-9 rounded-lg"
                    data-testid="admin-compose-user-search"
                  />
                </div>
                {results.length > 0 && (
                  <ul className="mt-2 bg-white border border-[#E5E5E5] rounded-lg max-h-48 overflow-y-auto divide-y divide-[#F0F0F0]" data-testid="admin-compose-search-results">
                    {results.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => add(u)}
                          className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] flex justify-between items-center"
                          data-testid={`admin-compose-result-${u.id}`}
                        >
                          <div>
                            <div className="text-sm font-medium">{u.name || "—"}</div>
                            <div className="text-xs text-[#6B6B6B] font-mono">{u.email}</div>
                          </div>
                          {u.role === "admin" && (
                            <Badge className="bg-[#C9A227]/20 text-[#8A6A00] border-[#C9A227]/40 text-[10px]">
                              admin
                            </Badge>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {search.trim().length >= 2 && !searching && results.length === 0 && (
                  <p className="text-xs text-[#6B6B6B] mt-1">No matches.</p>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Title *
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                className="mt-1 rounded-lg"
                data-testid="admin-compose-title"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Message *
              </Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                maxLength={8000}
                className="mt-1 rounded-lg"
                data-testid="admin-compose-body"
              />
              <p className="mt-1 text-[11px] text-[#6B6B6B] text-right">{body.length}/8000</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Optional link <span className="font-normal normal-case text-[#6B6B6B]">(in-app path, e.g. /events)</span>
              </Label>
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                maxLength={500}
                placeholder="/events"
                className="mt-1 rounded-lg"
                data-testid="admin-compose-link"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={submitOrConfirm}
              disabled={submitting}
              className={`rounded-full text-white ${mode === "broadcast" ? "bg-[#C1272D] hover:bg-[#0E0E0E]" : "bg-[#0E0E0E] hover:bg-[#C1272D]"}`}
              data-testid="admin-compose-send"
            >
              {submitting ? "Sending…" : mode === "broadcast" ? "Broadcast" : `Send to ${selected.length} user${selected.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={broadcastConfirmOpen} onOpenChange={setBroadcastConfirmOpen}>
        <AlertDialogContent data-testid="admin-compose-broadcast-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Broadcast to every user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will drop the message into every non-admin user's inbox.
              Users who have opted out of broadcasts in Settings will not receive it.
              You can't undo a broadcast.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setBroadcastConfirmOpen(false); send(); }}
              className="bg-[#C1272D] hover:bg-[#0E0E0E] text-white"
              data-testid="admin-compose-broadcast-confirm-btn"
            >
              Yes, broadcast
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
